import React from "react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BadgeCheck } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useActiveJob } from "@/contexts/ActiveJobContext";
import { useApplication } from "@/contexts/ApplicationContext";

interface PersonalDetailsTabsProps {
  activeTab: string;
  onTabChange: (value: string) => void;
  submittedTabs: string[];
  children: React.ReactNode;
}

const PersonalDetailsTabs: React.FC<PersonalDetailsTabsProps> = ({
  activeTab,
  onTabChange,
  submittedTabs,
  children,
}) => {
  const isMobile = useIsMobile();
  const { job } = useActiveJob();
  const { application } = useApplication();

  const segmentLabelMap: Record<string, string> = {
    "identity-confirmation": isMobile ? "Identity" : "Identity Confirmation",
    "bank-details": isMobile ? "Bank" : "Bank Details",
    "next-of-kin": isMobile ? "Next of Kin" : "Next of Kin",
    "tax-details": isMobile ? "Tax" : "Tax Details",
    "address-details": isMobile ? "Address" : "Address Details",
    headshot: isMobile ? "Photo" : "Headshot",
    "vehicle-release-form": isMobile
      ? "Vehicle Release"
      : "Vehicle Release Form",
  };

  const personalStage = job?.stages?.find((s) => s.id === "personal-details");
  const needsVan = !!application?.basicDetails?.needsVan;

  const stageTabs = (personalStage?.segments || []).map((seg) => ({
    value: seg.id,
    // Prefer stage-provided label; fallback to our map, then id
    label: seg.label || segmentLabelMap[seg.id] || seg.id,
    // Show VRF only if needsVan
    visible: seg.id === "vehicle-release-form" ? needsVan : true,
  }));

  // Fallback to known order if stage missing/empty
  const fallbackOrder = [
    "identity-confirmation",
    "bank-details",
    "next-of-kin",
    "tax-details",
    "address-details",
    "headshot",
    "vehicle-release-form",
  ];

  const fallbackTabs = (!stageTabs.length ? fallbackOrder : []).map((id) => ({
    value: id,
    label: segmentLabelMap[id] || id,
    // If stage missing, show all fallback items; otherwise visible from stage
    visible: id === "vehicle-release-form" ? needsVan : true,
  }));

  // Base tabs constructed from stage or fallback
  let baseTabs = [...stageTabs, ...fallbackTabs];

  const hasIdentityInBase = baseTabs.some(
    (t) => t.value === "identity-confirmation"
  );
  if (!hasIdentityInBase) {
    baseTabs = [
      {
        value: "identity-confirmation",
        label: segmentLabelMap["identity-confirmation"],
        visible: true,
      },
      ...baseTabs,
    ];
  }

  // Add VRF as a final form only if user needs van and it's not already present
  const hasVRFInBase = baseTabs.some(
    (t) => t.value === "vehicle-release-form" && t.visible
  );
  const maybeFinalForms =
    needsVan && !hasVRFInBase
      ? [
          {
            value: "vehicle-release-form",
            label: segmentLabelMap["vehicle-release-form"],
            visible: true,
          },
        ]
      : [];

  const tabConfig = [...baseTabs, ...maybeFinalForms];
  const visibleTabs = tabConfig.filter((t) => t.visible);

  return (
    <Tabs value={activeTab} onValueChange={onTabChange}>
      <div className="w-full overflow-x-auto">
        <TabsList className={`w-full flex ${isMobile ? "gap-1" : ""}`}>
          {visibleTabs.map((tab) => {
            const isSubmitted = submittedTabs.includes(tab.value);
            const isDisabled = tab.value !== activeTab; // Only active is clickable
            const triggerClasses = [
              "group flex-1 min-w-0",
              isSubmitted ? "bg-green-100" : "",
              isDisabled && !isSubmitted ? "opacity-50 cursor-not-allowed" : "",
            ]
              .filter(Boolean)
              .join(" ");

            return (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className={triggerClasses}
                disabled={isDisabled}
              >
                <div
                  className="flex w-full min-w-0 items-center justify-center gap-1 overflow-hidden text-ellipsis whitespace-nowrap"
                  title={typeof tab.label === "string" ? tab.label : undefined}
                >
                  {isSubmitted ? (
                    <BadgeCheck
                      className="h-4 w-4 text-green-600 shrink-0"
                      aria-hidden="true"
                    />
                  ) : null}
                  <span className="truncate">{tab.label}</span>
                </div>
              </TabsTrigger>
            );
          })}
        </TabsList>
      </div>
      {children}
    </Tabs>
  );
};

export default PersonalDetailsTabs;
